/**
 * Detail pane for an Other Statistics account — the same right-hand pane the
 * money accounts open, with the same driver picker (DriverSelect, fed the stat
 * catalog the API serves), a driver-inputs block that follows the chosen
 * driver, and a Months tab listing plan / driver / keyed / actual / LY with a
 * per-month note saying where the driven number came from.
 *
 * Reads the selected row and the band's write handlers from
 * otherStatsSession — the band owns the fetch; this pane never loads on its own.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, PanelRightClose, RotateCcw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import type { StatDriverConfig, StatDriverDef, StatDriverType, StatRateSource } from '../../api';
import type { DriverType } from '../../types';
import { MONTH_LABELS } from '../../types';
import { DriverSelect } from './DriverSelect';
import type { DriverCatalogEntry } from './driverCatalog';
import { SourceAccountPicker } from './SourceAccountPicker';
import { describeStatDriver, formatStatNumber } from '../../utils/otherStatsGrid';
import { selectStatAccount, useOtherStatsSession } from './otherStatsSession';

interface StatAccountDetailPanelProps {
  isPoppedOut?: boolean;
  onTogglePopout?: () => void;
}

type Tab = 'driver' | 'months';

const RATE_SOURCE_LABEL: Record<StatRateSource, { rooms: string; revenue: string }> = {
  ly: { rooms: 'Last year’s rate, month by month', revenue: 'Last year’s $ per unit, month by month' },
  ttm: { rooms: 'Trailing-twelve rate', revenue: 'Trailing-twelve $ per unit' },
  manual: { rooms: 'Rate I key', revenue: '$ per unit I key' },
};

/** The API's stat driver catalog in the picker's row shape. */
function toCatalog(defs: StatDriverDef[]): { entries: DriverCatalogEntry[]; groups: { name: string; hint: string }[] } {
  const groups: { name: string; hint: string }[] = [];
  const hints: Record<string, string> = {
    'Enter Manually': 'you type the number',
    'Driven by History': 'last year, trailing twelve',
    'Driven by Budget': 'follows the plan',
    'Spread a Total': 'one number for the year',
    'Driven by Rooms': 'scales with occupancy',
    'Driven by Revenue': 'units from dollars',
  };
  const entries = defs.map((d) => {
    if (!groups.some((g) => g.name === d.group)) groups.push({ name: d.group, hint: hints[d.group] || '' });
    return {
      // Stat driver ids ride through the money picker's DriverType-typed props.
      id: d.id as unknown as DriverType,
      group: d.group,
      label: d.label,
      desc: d.desc,
      tag: d.tag,
      inputHint: '',
      formula: '',
    };
  });
  return { entries, groups };
}

export function StatAccountDetailPanel({ isPoppedOut = false, onTogglePopout }: StatAccountDetailPanelProps) {
  const { row, published } = useOtherStatsSession();
  const [tab, setTab] = useState<Tab>('driver');
  const defs = useMemo(() => published?.driverDefs || [], [published]);
  const catalog = useMemo(() => toCatalog(defs), [defs]);
  const account = row?.account ?? null;
  const driver = account?.driver ?? null;
  const canEdit = !!published?.canEdit;
  const isBudget = !!published?.isBudget;

  // Local driver form — seeded from the row, re-seeded when the row changes.
  const [driverType, setDriverType] = useState<StatDriverType>(driver?.driverType || 'manual');
  const [input, setInput] = useState<string>(driver?.driverInput === null || driver?.driverInput === undefined ? '' : String(driver.driverInput));
  const [rateSource, setRateSource] = useState<StatRateSource | null>(driver?.rateSource || null);
  const [sourceIds, setSourceIds] = useState<number[]>(driver?.sourceChartOfAccountIds || []);
  const [saving, setSaving] = useState(false);
  const seedKey = `${account?.chartOfAccountId}|${driver?.driverType}|${driver?.driverInput}|${driver?.rateSource}|${(driver?.sourceChartOfAccountIds || []).join(',')}`;
  const lastSeed = useRef(seedKey);
  useEffect(() => {
    if (lastSeed.current === seedKey) return;
    lastSeed.current = seedKey;
    setDriverType(driver?.driverType || 'manual');
    setInput(driver?.driverInput === null || driver?.driverInput === undefined ? '' : String(driver.driverInput));
    setRateSource(driver?.rateSource || null);
    setSourceIds(driver?.sourceChartOfAccountIds || []);
  }, [seedKey, driver]);
  useEffect(() => { setTab('driver'); }, [account?.chartOfAccountId]);

  const def = defs.find((d) => d.id === driverType);
  const effectiveRate: StatRateSource | null = def?.rateSources ? (rateSource && def.rateSources.includes(rateSource) ? rateSource : def.rateSources[0]) : null;
  const inputKind: 'adjustPct' | 'annualTotal' | 'rate' | 'dollarsPerUnit' | null = (() => {
    if (!def || !def.input) return null;
    if (def.rateSources) return effectiveRate === 'manual' ? def.input : 'adjustPct';
    return def.input;
  })();
  const inputLabel = {
    adjustPct: 'Adjust by %',
    annualTotal: 'Annual total (units)',
    rate: driverType === 'per_arn' ? 'Units per available room night' : 'Units per occupied room night',
    dollarsPerUnit: '$ of revenue per unit (average check)',
  } as const;
  const inputPlaceholder = { adjustPct: '0', annualTotal: 'e.g. 12,000', rate: 'e.g. 1.25', dollarsPerUnit: 'e.g. 28.50' } as const;
  /** Drivers that can't run until the user has given them something. */
  const needsInputFirst = (t: StatDriverType, rs: StatRateSource | null, srcCount: number) => {
    const d = defs.find((x) => x.id === t);
    if (!d) return false;
    if (d.input === 'annualTotal') return true;
    if (d.rateSources && rs === 'manual') return true;
    if (d.needsSourceAccounts && srcCount === 0) return true;
    return false;
  };

  const parseInput = (): number | null | 'error' => {
    const text = input.replace(/[,\s%$]/g, '');
    if (text === '') return null;
    const n = Number(text);
    return Number.isFinite(n) ? n : 'error';
  };

  const save = async (next: { driverType: StatDriverType; rateSource: StatRateSource | null; input: number | null; sourceIds: number[] }) => {
    if (!row || !published) return;
    const d = defs.find((x) => x.id === next.driverType);
    const config: StatDriverConfig = {
      driverType: next.driverType,
      driverInput: next.input,
      rateSource: d?.rateSources ? next.rateSource : null,
      sourceChartOfAccountIds: d?.needsSourceAccounts ? next.sourceIds : [],
    };
    setSaving(true);
    try {
      await published.saveDriver(row, config);
    } catch (e) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Could not save driver');
    } finally {
      setSaving(false);
    }
  };

  // Picking a driver applies it straight away when it can run without input
  // (LY, TTM, budget…); drivers that need a number wait for Apply.
  const handlePick = async (next: StatDriverType) => {
    if (!canEdit) return;
    const d = defs.find((x) => x.id === next);
    const nextRate: StatRateSource | null = d?.rateSources ? (rateSource && d.rateSources.includes(rateSource) ? rateSource : d.rateSources[0]) : null;
    setDriverType(next);
    setRateSource(nextRate);
    if (d?.input !== def?.input) setInput('');
    if (next === 'manual') { await save({ driverType: 'manual', rateSource: null, input: null, sourceIds: [] }); return; }
    if (needsInputFirst(next, nextRate, sourceIds.length)) return;
    await save({ driverType: next, rateSource: nextRate, input: null, sourceIds });
  };

  const handleApply = async () => {
    if (!canEdit || driverType === 'manual') return;
    const parsed = parseInput();
    if (parsed === 'error') { toast.error('Enter a number'); return; }
    if ((inputKind === 'annualTotal' || inputKind === 'rate' || inputKind === 'dollarsPerUnit') && parsed === null) { toast.error(`Enter ${inputLabel[inputKind].toLowerCase()}`); return; }
    if ((inputKind === 'rate' || inputKind === 'dollarsPerUnit') && parsed !== null && parsed <= 0) { toast.error('Must be greater than zero'); return; }
    if (def?.needsSourceAccounts && sourceIds.length === 0) { toast.error('Pick at least one revenue account'); return; }
    await save({ driverType, rateSource: effectiveRate, input: parsed, sourceIds });
  };

  const dirty = (() => {
    if (!driver) return driverType !== 'manual';
    const parsed = parseInput();
    const cur = driver.driverInput ?? null;
    return driverType !== driver.driverType
      || (parsed !== 'error' && (parsed ?? null) !== cur)
      || (def?.rateSources ? effectiveRate !== (driver.rateSource || def.rateSources[0]) : false)
      || (def?.needsSourceAccounts ? sourceIds.join(',') !== (driver.sourceChartOfAccountIds || []).join(',') : false);
  })();

  const revenueOptions = useMemo(
    () => (published?.revenueAccounts || []).map((a) => ({ chartOfAccountId: a.chartOfAccountId, accountName: a.accountName, accountNumber: a.accountNumber || '', department: a.department || '' })),
    [published]
  );

  const clearKeyed = async (monthIdx?: number) => {
    if (!row || !published || !published.year) return;
    const items = row.cells
      .map((c, idx) => (c.keyed !== null && (monthIdx === undefined || idx === monthIdx) ? { chartOfAccountId: row.account.chartOfAccountId, year: published.year as number, month: idx + 1, value: null } : null))
      .filter((x): x is { chartOfAccountId: number; year: number; month: number; value: null } => x !== null);
    if (!items.length) return;
    await published.saveKeyed(items);
  };

  if (!row || !account) {
    return (
      <div className={`${isPoppedOut ? 'w-full min-h-screen' : 'w-[420px] border-l'} border-slate-200 bg-white flex flex-col`}>
        <div className="flex-1 flex items-center justify-center p-8 text-center text-sm text-slate-400">Statistics account no longer on screen.</div>
      </div>
    );
  }

  const chip = describeStatDriver(driver, defs.find((d) => d.id === driver?.driverType)?.label);
  const keyedCount = row.cells.filter((c) => c.keyed !== null).length;

  return (
    <div
      className={`${isPoppedOut ? 'w-full min-h-screen' : 'w-[420px] border-l'} border-slate-200 bg-white flex flex-col relative`}
      data-driver-takeover-host=""
    >
      {/* Header — same chrome as the money-account pane */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-800">{account.accountName}</h3>
          <div className="flex items-center gap-0.5">
            {onTogglePopout && (
              <button onClick={onTogglePopout} className="p-1 hover:bg-slate-100 rounded transition-colors" title={isPoppedOut ? 'Bring pane back into the workspace' : 'Pop out to a separate window'}>
                {isPoppedOut ? <PanelRightClose size={16} className="text-slate-400" /> : <ExternalLink size={16} className="text-slate-400" />}
              </button>
            )}
            {!isPoppedOut && (
              <button onClick={() => selectStatAccount(null)} className="p-1 hover:bg-slate-100 rounded transition-colors" aria-label="Close">
                <X size={18} className="text-slate-400" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>{account.accountType || 'Statistic'} • {account.department || 'No department'} • {account.accountNumber}</span>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${row.hasDriver ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`}>{chip}</span>
          {keyedCount > 0 && <span className="text-xs text-slate-400">{keyedCount} keyed month{keyedCount === 1 ? '' : 's'}{row.hasDriver ? ' override the driver' : ''}</span>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex" style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}>
        {(['driver', 'months'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="flex-1 py-2.5 text-sm font-medium capitalize transition-colors border-b-2"
            style={tab === t
              ? { color: 'var(--lodge-orange-500)', borderBottomColor: 'var(--lodge-orange-400)', marginBottom: -1 }
              : { color: 'var(--lodge-onyx-500)', borderBottomColor: 'transparent', marginBottom: -1 }}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {tab === 'driver' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Driver</label>
              <DriverSelect
                value={driverType as unknown as DriverType}
                accountType="Expense"
                onChange={(next) => handlePick(next as unknown as StatDriverType)}
                disabled={!canEdit}
                isSaving={saving}
                catalog={catalog}
              />
              {isBudget && driverType === 'budget' && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">This is a budget version — “Budget” reads the year’s existing financialBudget rows, which may be this very budget.</div>
              )}
            </div>

            {def?.rateSources && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Rate source</label>
                <div className="flex flex-col gap-1.5">
                  {def.rateSources.map((rs) => (
                    <label key={rs} className={`flex items-center gap-2 text-sm text-slate-700 ${canEdit ? 'cursor-pointer' : ''}`}>
                      <input type="radio" name="statRateSource" disabled={!canEdit} checked={effectiveRate === rs} onChange={() => { setRateSource(rs); if ((rs === 'manual') !== (effectiveRate === 'manual')) setInput(''); }} />
                      <span>{RATE_SOURCE_LABEL[rs][driverType === 'per_revenue' ? 'revenue' : 'rooms']}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {inputKind && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{inputLabel[inputKind]}</label>
                <div className="flex items-center gap-2">
                  {inputKind === 'dollarsPerUnit' && <span className="text-slate-400">$</span>}
                  <input
                    type="text"
                    inputMode="decimal"
                    value={input}
                    disabled={!canEdit}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApply(); } }}
                    placeholder={inputPlaceholder[inputKind]}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-right tabular-nums focus:outline-none"
                    style={{ borderColor: dirty ? 'var(--lodge-orange-300)' : undefined }}
                  />
                  {inputKind === 'adjustPct' && <span className="text-slate-400">%</span>}
                </div>
                {inputKind === 'adjustPct' && <div className="text-xs text-slate-400 mt-1">Blank for no change; 5 = +5 %, -3 = −3 %.</div>}
              </div>
            )}

            {def?.needsSourceAccounts && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Revenue accounts that produce this statistic</label>
                {revenueOptions.length === 0
                  ? <div className="text-xs text-slate-400 italic">No revenue accounts loaded.</div>
                  : <SourceAccountPicker accounts={revenueOptions} selectedIds={sourceIds} onChange={setSourceIds} disabled={!canEdit} dense maxListHeight={200} footerVerb="feed this statistic" />}
              </div>
            )}

            {canEdit && driverType !== 'manual' && (
              <button
                type="button"
                disabled={saving || !dirty}
                onClick={handleApply}
                className="w-full py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40"
                style={{ background: 'var(--lodge-orange-500)' }}
              >
                {saving ? 'Saving…' : dirty ? 'Apply driver' : 'Applied'}
              </button>
            )}
            {!canEdit && <div className="text-xs text-slate-400">Read-only version.</div>}

            {row.hasDriver && (
              <div>
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">How each month is calculated</div>
                <div className="rounded-lg border border-slate-100 divide-y divide-slate-100">
                  {row.cells.map((cell, idx) => (
                    <div key={MONTH_LABELS[idx]} className="flex items-baseline gap-2 px-3 py-1.5 text-xs">
                      <span className="w-8 text-slate-400">{MONTH_LABELS[idx]}</span>
                      <span className="w-16 text-right tabular-nums font-medium text-slate-700">{cell.driven === null ? '—' : formatStatNumber(cell.driven)}</span>
                      <span className="flex-1 text-slate-500 truncate" title={cell.note}>{cell.note || (cell.driven === null ? 'No basis for this month' : '')}</span>
                      {cell.keyed !== null && <span className="text-[10px] font-medium" style={{ color: 'var(--lodge-orange-500)' }}>keyed {formatStatNumber(cell.keyed)}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'months' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Plan by month</div>
              {canEdit && keyedCount > 0 && (
                <button type="button" onClick={() => clearKeyed()} className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--lodge-orange-500)' }}>
                  <RotateCcw size={11} /> Clear keyed
                </button>
              )}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-400">
                  <th className="text-left font-medium py-1">Month</th>
                  <th className="text-right font-medium py-1">Plan</th>
                  <th className="text-right font-medium py-1">Driver</th>
                  <th className="text-right font-medium py-1">Actual</th>
                  <th className="text-right font-medium py-1">LY</th>
                  <th className="text-right font-medium py-1">{isBudget ? 'LY Bud' : 'Budget'}</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {row.cells.map((cell, idx) => (
                  <tr key={MONTH_LABELS[idx]} className="border-t border-slate-100">
                    <td className="py-1.5 text-slate-500">{MONTH_LABELS[idx]}</td>
                    <td className={`py-1.5 text-right tabular-nums ${cell.keyed !== null ? 'font-medium' : 'text-slate-700'}`} style={cell.keyed !== null ? { color: 'var(--lodge-orange-500)' } : undefined} title={cell.keyed !== null ? 'Keyed' : cell.note}>
                      {cell.plan === null ? '—' : formatStatNumber(cell.plan)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-slate-500" title={cell.note}>{cell.driven === null ? '—' : formatStatNumber(cell.driven)}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-500">{cell.actual === null ? '—' : formatStatNumber(cell.actual)}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-500">{cell.lyActual === null ? '—' : formatStatNumber(cell.lyActual)}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-500">{(isBudget ? cell.lyBudget : cell.budget) === null ? '—' : formatStatNumber(isBudget ? cell.lyBudget : cell.budget)}</td>
                    <td className="py-1.5 text-right">
                      {canEdit && cell.keyed !== null && (
                        <button type="button" onClick={() => clearKeyed(idx)} className="text-[10px] px-1 rounded hover:bg-red-50" style={{ color: 'var(--lodge-orange-500)' }} title="Clear the keyed value">
                          reset
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-xs text-slate-400 mt-3">Key values in the grid row. Orange months are keyed and override the driver.</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StatAccountDetailPanel;
